import { Box, Grid, Input, Typography, Checkbox } from "@mui/material";
import style from "../styles/detailedConsultation.module.scss";
import { ComboBoxCustom } from "shared/components/ComboBoxCustom";
import { RouterGuard } from "shared/components";
import { accesRoles, IElectronicDocumentLine } from "shared/models";
import { IDetailedParametersServices } from "shared/models/store/IElectronicDocuments";
import { useTableResults } from "shared/hooks/useTableResults";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Dispatch } from "shared/store/store";
import { DocumentLinesTable } from "./DocumentLinesTable";
import { ProvitionServiceDateField } from "./ProvitionServiceDateField";
import { LinePaymentField } from "./LinePaymentField";
import { ViewDetailButton } from "./ViewDetailButton";
import Image from "shared/assets/index";
import { LABEL_ACTIVIDAD } from "../helpers";
import { findActivityStatus } from "pages/read-documents/helper/findActivityStatus";
import { Fragment } from "react";
import { ShowBalanceHistory } from "./ShowBalanceHistory";
import { ShowProvisionHistory } from "./ShowProvisionHistory";
import styles from "../styles/showPayment.module.scss";
import Swal from "sweetalert2";
import { putFinancialProcess } from "shared/api/services/putFinancialProcess";

interface Props {
  detailedParameters: any;
  documentLines?: IElectronicDocumentLine[];
  showDocumentLines?: boolean;
  docState?: IDetailedParametersServices;
}

export const TabDetails = ({
  detailedParameters,
  documentLines,
  showDocumentLines,
  docState,
}: Props) => {
  const { handleUserAssignment, validateUserAuditor } = useTableResults();
  const { idDocument } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<Dispatch>();

  const handleFinancialProcessChange = async (
    checked: boolean,
    docId: string,
  ) => {
    const result = await Swal.fire({
      title: checked
        ? "¿Aprobar para proceso financiero?"
        : "¿Desaprobar para proceso financiero?",
      text: "Esta acción actualizará el estado de la factura",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, continuar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await putFinancialProcess(docId, checked);
        dispatch.invoiceData.modifyIsFinancialProcess(checked);
        Swal.fire({
          title: "Actualizado",
          text: "El campo ha sido actualizado exitosamente",
          icon: "success",
          timer: 2000,
        });
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: "No se pudo actualizar el campo",
          icon: "error",
          timer: 2000,
        });
      }
    }
  };

  const shouldShowFinancialProcessField = () => {
    if (!docState) {
      return false;
    }

    const timelineCode = docState?.timeline?.lastTimelineStage?.code;
    const activityCode = docState?.state?.code;
    const consolidatedInNoAgreementValue =
      docState?.consolidatedInNoAgreementValue;
    const paymentAuthorizationTotalValue =
      docState?.paymentAuthorizationTotalValue;

    // Condición 1: Autorizada para pagos
    const condition1 =
      timelineCode === "ON_PAYMENT_AUTH_INVOICE" && activityCode === "9.3";

    // Condición 2: Glosas consolidadas
    const condition2 =
      timelineCode === "CONCILIATED_INVOICE" &&
      activityCode === "7.2.3" &&
      consolidatedInNoAgreementValue !== undefined &&
      consolidatedInNoAgreementValue > 0 &&
      paymentAuthorizationTotalValue !== undefined &&
      paymentAuthorizationTotalValue > 0;

    return condition1 || condition2;
  };

  return (
    <Fragment>
      {detailedParameters.length > 0 &&
        detailedParameters.map((item: any, index: number) => (
          <div
            className={`${style.containerBody} containerForm`}
            key={index}
            data-testid={"tabs-details"}
          >
            <div className="containerTitleCardTabs">
              <img src={item.icon} alt="" />
              <Typography>{item.title}</Typography>
            </div>

            {item.hasContent ? (
              <Grid
                container
                direction={"row"}
                className={style.contentDetails}
              >
                {item.columns?.map((column: any, index: number) => {
                  if (["assignedTo"].includes(column.idLabel)) {
                    return (
                      <RouterGuard
                        roles={accesRoles.usersSSSA}
                        type="component"
                        key={index}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={4}
                          md={3}
                          className={style.field}
                          key={index}
                        >
                          <Typography className={style.titleDetails}>
                            {column.label}:{" "}
                          </Typography>
                          <ComboBoxCustom
                            handleChange={handleUserAssignment}
                            data={item.content.users}
                            idsDoc={[Number(idDocument)]}
                            currentValue={item.content.currentUser}
                            placeholder="Asignar"
                            focusSelected={true}
                            isDisabled={
                              docState?.state?.code === "1.3" ||
                              docState?.state?.code === "1.4" ||
                              validateUserAuditor()
                            }
                          />
                        </Grid>
                      </RouterGuard>
                    );
                  }

                  if (["contrato"].includes(column.idLabel)) {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={style.field}
                        key={index}
                        data-testid="grid_contrato"
                      >
                        <Typography className={style.titleDetails}>
                          {column.label}:{" "}
                        </Typography>
                        <div style={{ display: "inline-flex" }}>
                          <label>
                            <input
                              type="radio"
                              name="contrato"
                              value="Si"
                              disabled
                              checked={item.content.contratoQuestion === true}
                            ></input>
                            Si
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="contrato"
                              value="No"
                              disabled
                              checked={item.content.contratoQuestion === false}
                            ></input>
                            No
                          </label>
                        </div>
                      </Grid>
                    );
                  }

                  if (["provisionContable"].includes(column.idLabel)) {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={style.field}
                        key={index}
                        data-testid="grid_provisionContable"
                      >
                        <Typography className={style.titleDetails}>
                          {column.label}:{" "}
                        </Typography>
                        <Box className={styles.containerInputPayment}>
                          <Box
                            className={styles.inputEditonDetailsContainer}
                            data-testid="content_provision"
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <label style={{ marginRight: "50px" }}>
                                <input
                                  type="radio"
                                  name="provisionContable"
                                  value="Si"
                                  disabled
                                  checked={
                                    item.content.provisionContableQuestion ===
                                    true
                                  }
                                ></input>
                                Si
                              </label>
                              <label style={{ marginRight: "30px" }}>
                                <input
                                  type="radio"
                                  name="provisionContable"
                                  value="No"
                                  disabled
                                  checked={
                                    item.content.provisionContableQuestion ===
                                    false
                                  }
                                ></input>
                                No
                              </label>
                              <ShowProvisionHistory
                              />
                            </div>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  }

                  if (["isFinancialProcess"].includes(column.idLabel)) {
                    if (!shouldShowFinancialProcessField()) {
                      return null;
                    }
                    return (
                      <RouterGuard
                        roles={accesRoles.validator}
                        type="component"
                        key={index}
                      >
                        <Grid
                          item
                          xs={12}
                          sm={4}
                          md={3}
                          className={style.field}
                          key={index}
                          data-testid="grid_isFinancialProcess"
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0px",
                            }}
                          >
                            <Typography
                              className={style.titleDetails}
                              style={{ width: "auto" }}
                            >
                              {column.label}:
                            </Typography>
                            <Checkbox
                              checked={item.content[column.idLabel] || false}
                              style={{ marginLeft: "4px" }}
                              onChange={(e) =>
                                handleFinancialProcessChange(
                                  e.target.checked,
                                  idDocument!,
                                )
                              }
                              disabled={
                                docState?.state?.code === "1.3" ||
                                docState?.state?.code === "1.4"
                              }
                            />
                          </div>
                        </Grid>
                      </RouterGuard>
                    );
                  }

                  if (
                    ["fechaInicioServicios", "fechaFinalServicios"].includes(
                      column.idLabel,
                    )
                  ) {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={style.field}
                        key={index}
                        data-testid="grid_fecha"
                      >
                        <Typography className={style.titleDetails}>
                          {column.label}:{" "}
                        </Typography>
                        <ProvitionServiceDateField
                          value={item.content[column.idLabel]}
                          property={column.idLabel}
                          index={index}
                          docState={docState}
                          role={accesRoles.auditor}
                        />
                      </Grid>
                    );
                  }

                  if (["lineaDePago"].includes(column.idLabel)) {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={style.field}
                        key={index}
                      >
                        <Typography className={style.titleDetails}>
                          {column.label}:{" "}
                        </Typography>
                        <LinePaymentField
                          value={item.content[column.idLabel]}
                          property={column.idLabel}
                          index={index}
                          docState={docState}
                        />
                      </Grid>
                    );
                  }

                  if (["saldoFactura"].includes(column.idLabel)) {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={style.field}
                        key={index}
                      >
                        <Typography className={style.titleDetails}>
                          {column.label}:{" "}
                        </Typography>
                        <Box className={styles.containerInputPayment}>
                          <Box
                            className={styles.inputEditonDetailsContainer}
                            data-testid="content_line_payment"
                          >
                            <input
                              data-testid={`input_value_${index}`}
                              className={styles.inputDetails}
                              type="text"
                              disabled
                              value={item.content[column.idLabel]}
                            />
                            <ShowBalanceHistory docState={docState} />
                          </Box>
                        </Box>
                      </Grid>
                    );
                  }

                  if (["nroContrato"].includes(column.idLabel)) {
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={style.field}
                        key={index}
                      >
                        <Typography className={style.titleDetails}>
                          {column.label}:{" "}
                        </Typography>
                        <ViewDetailButton
                          index={index}
                          value={item}
                          column={column}
                          roles={accesRoles.usersSSSA}
                          titleTooltip="Ver detalle del  contrato"
                          onClickIcon={() =>
                            navigate(
                              `/consulta_documentos_electronicos/detalle_documento/${idDocument}/ver_detalle_contrato/${item?.content[column.idLabel]}`,
                            )
                          }
                          disabledButton={false}
                          idDocument={idDocument}
                          typeIcon={Image.iconoVer}
                        />
                      </Grid>
                    );
                  }

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={3}
                      className={style.field}
                      key={index}
                    >
                      <Typography className={style.titleDetails}>
                        {column.label}:{" "}
                      </Typography>
                      <Input
                        startAdornment={
                          column.label === LABEL_ACTIVIDAD && <span></span>
                        }
                        data-testid={`input_value_${index}`}
                        className={`${style.inputDetails} ${column.label === LABEL_ACTIVIDAD && style[`${findActivityStatus(docState)}T`]}`}
                        type="text"
                        disabled
                        value={item.content[column.idLabel]}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box className={style.containerResults}>
                Aún no se ha cargado la información correspondiente de esta
                sección
              </Box>
            )}
          </div>
        ))}
      {showDocumentLines ? (
        <DocumentLinesTable documentLines={documentLines} />
      ) : null}
    </Fragment>
  );
};
